import { Tooltip } from 'bootstrap'

// TODO.md T-98. **Bootstrap tooltips, initialised without leaking the previous set.**
//
// 🚨 **Two separate leaks, both measured.** `initTooltips()` ran from the QC pack *and* from
// `query_data.js.erb` — so on every filter change — and nothing disposed the previous instances. Bootstrap 5
// keeps them in a **strong** `Map`, so every Tooltip ever created on the page stayed reachable after
// `.html()` replaced the table. And since `F-136` each non-empty one also pinned a `cloneNode(true)`
// subtree, built eagerly at init. Context: ~14 381 chips in one 12-month window on the dev database.
//
// ✅ **Fixed two ways:**
// 1. **Dispose before re-creating.** `packs/dashboard/myyja.js` already did this correctly in its
//    `drawCallback`; the QC path did not, which is why that one grew without bound.
// 2. **The title is a FUNCTION.** Bootstrap resolves `_config.title` through `_resolvePossibleFunction` on
//    each `show()`, so the clone happens only for the chip actually hovered — one subtree instead of
//    ~1 400, and none of them retained.
//
// ⚠️ **Delegation (`new Tooltip(container, { selector })`) was considered and NOT taken**, though `T-98`
// lists it as the strongest option. It would replace 14 381 instances with one, but it also hands Bootstrap
// the `title` config for *every* matching element — including the plain triggers that currently keep
// `new Tooltip(el)` and get their text from their own `title` attribute. Getting that wrong turns a working
// tooltip into an empty one, and Bootstrap's title/`data-bs-original-title` handling is not something to
// infer. The per-draw instance cost that delegation would remove is real but separate: it is listeners, not
// retention, and it should be measured on its own rather than ridden in on a leak fix.
//
// 📌 **Lives in `src/` because it was never QC-specific.** It was a `window.*` global defined in the QC
// pack while initialising every `[data-bs-toggle="tooltip"]` on the page, and the dashboard carried its own
// partial copy of the same loop. `.qc-tooltip-body` is the only QC-specific thing in it.
const TRIGGER_SELECTOR = '[data-bs-toggle="tooltip"]'

// 🔑 **Resolved on each `show()`, not at init.** `this` is the trigger element.
// 📌 **Cloned, and the `d-none` dropped on the clone.** Bootstrap *moves* an element it is given into the
// tooltip; cloning leaves the original in the chip so re-showing still works, and the hidden class has to go
// or the content would be invisible inside the tooltip it was moved into (`F-136`).
// 🔑 **An Element is appended as-is, so no string is ever re-parsed and `sanitize` stops being
// load-bearing** — the body comes from `_quality_schedule_tooltip.html.erb`, where `<%=` does the escaping.
function cloneTooltipBody() {
  const body = this.querySelector('.qc-tooltip-body')
  if (!body) return ''

  const content = body.cloneNode(true)
  content.classList.remove('d-none')
  return content
}

// 🚨 **Dispose BEFORE the HTML is replaced, or nothing is disposed at all.** `/simplify` measured the
// ordering: `query_data.js.erb` does `.html(...)` on line 1 and `initTooltips()` on line 4, so by the time
// the dispose loop ran the old chips were already detached and `Tooltip.getInstance(newChip)` returned
// `null` for every one. **The ~14 381 old instances stayed in Bootstrap's strong `elementMap`** — so T-98's
// dispose half bought ~43 000 operations per refetch and left the retention it was filed for unchanged.
// 🔑 What it *did* dispose was the surviving non-schedule triggers, which were never leaking.
// 📌 The lazy `title` half of T-98 was a real win and is untouched: the eager `cloneNode(true)` per chip is
// gone, which is the ~1 400 retained subtrees.
export function disposeTooltips(root = document) {
  root.querySelectorAll(TRIGGER_SELECTOR).forEach(function (trigger) {
    const existing = Tooltip.getInstance(trigger)
    if (existing) existing.dispose()
  })
}

export default function initTooltips(root = document) {
  root.querySelectorAll(TRIGGER_SELECTOR).forEach(function (trigger) {
    // Still idempotent: a caller that re-inits over surviving markup (the dashboard's `drawCallback`) would
    // otherwise stack a second instance on the same element.
    const existing = Tooltip.getInstance(trigger)
    if (existing) existing.dispose()

    const body = trigger.querySelector('.qc-tooltip-body')
    if (!body || !body.textContent.trim()) {
      // No rich body: Bootstrap takes the text from the element's own `title` attribute, as before.
      new Tooltip(trigger)
      return
    }

    new Tooltip(trigger, { title: cloneTooltipBody, html: true })
  })
}
